// Photos (M12): screenshots as tiles, by day, and a viewer that shows one large with what it is
// of, whose it is and when, a "Show on map" link and the original file.
import { photoUrl, type ChronicleEntry, type PhotoInfo } from '@brimstone/shared';
import { useEffect, useRef, useState } from 'react';
import { formatClock } from '../lib/time';
import { ShowOnMap } from './ShowOnMap';

/** What a photo is of: its Chronicle entry, else where it was taken. */
export function photoTitle(p: PhotoInfo): string {
  if (p.event) return p.event.text;
  const where = [p.subzone, p.zone].filter(Boolean).join(', ');
  return where || 'Screenshot';
}

/** Whose it is, when, and the boss fight or target it shows. */
function photoMeta(p: PhotoInfo): string {
  return [
    p.characterName ?? 'Unknown character',
    formatClock(p.takenAt),
    p.encounter && `fight: ${p.encounter}`,
    p.target && `target: ${p.target}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A Chronicle entry's photos, for the viewer: captioned by the entry. */
export function entryPhotos(e: ChronicleEntry): PhotoInfo[] {
  return e.photoIds.map((id) => ({
    id,
    takenAt: e.occurredAt,
    characterId: e.characterId,
    characterName: e.characterName,
    event: { id: e.id, type: e.type, text: e.text },
    zone: null,
    subzone: null,
    encounter: null,
    target: null,
    map: e.map,
    width: null,
    height: null,
    error: null,
  }));
}

/** One photo full size, with the ones beside it a key press away. */
export function PhotoViewer({
  photos,
  index,
  onIndex,
  onClose,
}: {
  photos: PhotoInfo[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const close = useRef<HTMLButtonElement>(null);
  const p = photos[index]!;
  const has = { prev: index > 0, next: index < photos.length - 1 };

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    close.current?.focus();
    return () => before?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && has.prev) onIndex(index - 1);
      else if (e.key === 'ArrowRight' && has.next) onIndex(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, has.prev, has.next, onIndex, onClose]);

  const button =
    'inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center border border-line bg-surface px-3 font-mono text-compact text-muted hover:text-text disabled:cursor-default disabled:opacity-40';
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photoTitle(p)}
      className="fixed inset-0 z-[2000] flex flex-col bg-bg/95"
    >
      <div className="flex items-center justify-between gap-3 border-b border-line bg-header px-4 py-2">
        <span className="font-mono text-label text-muted">
          {index + 1} / {photos.length}
        </span>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={button}
            disabled={!has.prev}
            aria-label="Previous photo"
            onClick={() => onIndex(index - 1)}
          >
            ←
          </button>
          <button
            type="button"
            className={button}
            disabled={!has.next}
            aria-label="Next photo"
            onClick={() => onIndex(index + 1)}
          >
            →
          </button>
          <a
            className={`${button} no-underline`}
            href={photoUrl(p.id, 'original')}
            target="_blank"
            rel="noreferrer"
          >
            ORIGINAL
          </a>
          <button ref={close} type="button" className={button} onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-2 sm:p-4">
        {p.error ? (
          <p className="m-0 text-secondary text-muted">
            <span className="font-mono text-danger">NOT READABLE</span> {p.error}
          </p>
        ) : (
          <img
            src={photoUrl(p.id, 'view')}
            alt={photoTitle(p)}
            className="max-h-full max-w-full object-contain"
          />
        )}
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-line bg-surface px-4 py-3">
        <div className="min-w-0">
          <div>{photoTitle(p)}</div>
          <div className="font-mono text-label text-muted">{photoMeta(p)}</div>
        </div>
        {p.map && <ShowOnMap link={p.map} character={p.characterId} />}
      </div>
    </div>
  );
}

function PhotoTile({ photo, onOpen }: { photo: PhotoInfo; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer flex-col border border-line bg-bg p-0 text-left text-text hover:border-muted"
    >
      <span className="block aspect-[16/10] w-full overflow-hidden bg-surface">
        {photo.error ? (
          <span className="flex h-full items-center justify-center px-2 text-center font-mono text-tag text-danger">
            NOT READABLE
          </span>
        ) : (
          <img
            src={photoUrl(photo.id, 'thumb')}
            alt=""
            loading="lazy"
            className="block h-full w-full object-cover"
          />
        )}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5 px-2 py-1.5">
        <span className="line-clamp-2 text-secondary">{photoTitle(photo)}</span>
        <span className="truncate font-mono text-tag text-muted">
          {photo.characterName ?? 'Unknown'} · {formatClock(photo.takenAt)}
        </span>
      </span>
    </button>
  );
}

/** "Friday 20 November", in local time. */
function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * Photos as tiles, newest first as given; `byDay` puts a header over each local day. One viewer
 * steps through all of them.
 */
export function PhotoGrid({
  photos,
  label,
  byDay = false,
  narrow = false,
}: {
  photos: PhotoInfo[];
  label: string;
  byDay?: boolean;
  /** Two columns at most: for side panels. */
  narrow?: boolean;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const groups: { day: string | null; items: { photo: PhotoInfo; index: number }[] }[] = [];
  photos.forEach((photo, index) => {
    const day = byDay ? dayLabel(photo.takenAt) : null;
    const last = groups.at(-1);
    if (last && last.day === day) last.items.push({ photo, index });
    else groups.push({ day, items: [{ photo, index }] });
  });
  return (
    <>
      <div aria-label={label} className="flex flex-col">
        {groups.map((g) => (
          <section key={g.day ?? 'all'} aria-label={g.day ?? undefined}>
            {g.day && (
              <h3 className="m-0 flex justify-between gap-3 border-b border-line bg-bg px-4 py-2 text-label font-normal uppercase tracking-label text-muted">
                <span>{g.day}</span>
                <span className="font-mono">{g.items.length}</span>
              </h3>
            )}
            <ul
              className={`m-0 grid list-none grid-cols-2 gap-2 p-4 ${narrow ? '' : 'sm:grid-cols-3 lg:grid-cols-4'}`}
            >
              {g.items.map(({ photo, index }) => (
                <li key={photo.id} className="min-w-0">
                  <PhotoTile photo={photo} onOpen={() => setOpen(index)} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      {open !== null && (
        <PhotoViewer photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

/** A Chronicle entry's photos as small thumbnails that open the viewer. */
export function EntryPhotos({ entry }: { entry: ChronicleEntry }) {
  const [open, setOpen] = useState<number | null>(null);
  if (entry.photoIds.length === 0) return null;
  const photos = entryPhotos(entry);
  return (
    <span className="mt-1.5 flex flex-wrap gap-1.5">
      {photos.map((p, i) => (
        <button
          key={p.id}
          type="button"
          aria-label={`Photo: ${entry.text}`}
          onClick={() => setOpen(i)}
          className="block h-11 w-[70px] cursor-pointer overflow-hidden border border-line bg-surface p-0 hover:border-muted"
        >
          <img
            src={photoUrl(p.id, 'thumb')}
            alt=""
            loading="lazy"
            className="block h-full w-full object-cover"
          />
        </button>
      ))}
      {open !== null && (
        <PhotoViewer photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </span>
  );
}
